import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction
} from 'mobx';
import * as React from 'react';
import { differenceInDays } from 'date-fns';

import { IRootStore } from 'shared/entities/store/rootStore';
import { apiUrls, urls } from 'shared/entities/domain';
import {
  PaymentInfoServer,
  PaymentParams,
  PaymentState,
  mapCalculatePayErrorToMessage,
  getAvailableSumPayments,
  CalculatePayError,
  GetPaymentInfoRequestParams
} from 'shared/entities/payment';
import { PaymentInfoModel, PaymentFillModel } from 'shared/models/payment';
import { IPaymentStore } from 'shared/entities/store/paymentStore';
import {
  AppNotificationType,
  BannerPriority
} from 'shared/entities/appNotifications';
import { AnalyticsEvent } from 'shared/entities/analytics';
import { LocalStorageKey } from 'shared/entities/localStorage';
import { localStorageHandler } from 'stores/localStorageHandler';
import TypedTransComponent from 'shared/components/TypedTransComponent';
import { FieldModel } from 'shared/models/form';
import ExternalLink from 'shared/newComponents/ExternalLink';
import {
  TypographyColor,
  TypographySize,
  TypographyType
} from 'shared/newEntities/components/Typography';
import { CabinetSplit } from 'shared/entities/cabinet';
import getBooleanValueFromLS from 'shared/utils/getBooleanValueFromLS';
import { LoadingStageModel } from 'shared/models/loadingStage';

import {
  PAYMENT_LINK_BANNER_ID,
  CREATED_INVOICE_BANNER_ID,
  DAYS_TO_SHOW_BANNER
} from './config';
import { getPaymentRequiredMessageKey } from './utils/getPaymentRequiredMessageKey';
export default class PaymentStore implements IPaymentStore {
  private rootStore: IRootStore;
  private _info: PaymentInfoModel | null = null;
  private _paymentFill: PaymentFillModel;
  readonly availableSumPayments: FieldModel<number[] | null> = new FieldModel(
    null
  );

  readonly getInfoStage: LoadingStageModel = new LoadingStageModel();
  readonly getLinkStage: LoadingStageModel = new LoadingStageModel();

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
    this._paymentFill = new PaymentFillModel(this, rootStore);
    makeObservable<PaymentStore, '_info' | 'getLink'>(this, {
      _info: observable.ref,

      info: computed,
      shouldPay: computed,
      isCorporate: computed,

      getInfo: action,
      getLink: action
    });
  }

  get currency(): '₽' | '$' {
    return this.rootStore.pluginsStore.internationalDomainTurnOn ? '$' : '₽';
  }

  get paymentFill(): PaymentFillModel {
    return this._paymentFill;
  }

  get shouldPay(): boolean | null {
    return this.info ? this.info.shouldPay : null;
  }

  get info(): PaymentInfoModel | null {
    return this._info;
  }

  get isCorporate(): boolean {
    return this.rootStore.cabinetStore.entity?.split === CabinetSplit.corporate;
  }

  showPaymentRequiredError = (): void => {
    const messageKey = getPaymentRequiredMessageKey({
      isELamaUser: this.rootStore.permissionsStore.isELamaUser,
      canViewBilling: this.rootStore.permissionsStore.canViewBilling
    });

    this.rootStore.appNotificationsStore.open({
      type: AppNotificationType.error,
      title: (t) =>
        t(`PaymentStore.notifications.${messageKey}`, { ns: 'stores' }),
      timeout: 10000
    });
  };

  saveStateBeforeOpenPaymentWindow = async (): Promise<void> => {
    if (!this.info) {
      return;
    }

    const data = {
      state: this.info.state,
      balance: this.info.billing.balance,
      price: this.paymentFill.priceToPay.value,
      ...this.paymentFill.paymentParams
    };

    localStorage.setItem(
      LocalStorageKey.paymentStateBeforePayment,
      JSON.stringify(data)
    );

    if (this.info.paymentsCount === 0) {
      localStorage.setItem(
        LocalStorageKey.withoutPaymentsStateBeforePayment,
        'true'
      );
    }
  };

  private handleLSPaymentsCount = async () => {
    if (!this.info) {
      return;
    }

    const withoutPayments = getBooleanValueFromLS(
      LocalStorageKey.withoutPaymentsStateBeforePayment
    );

    if (!withoutPayments) {
      return;
    }

    // на момент оплаты не было платежей, проверяем есть ли сейчас
    let hasPaymentCounts = false;
    if (this._info?.paymentsCount === null) {
      const response = await this.getPaymentsCount();
      if (!response.isError) {
        hasPaymentCounts = Boolean(response.data.paymentsCount);
      }
    } else {
      hasPaymentCounts = Boolean(this._info?.paymentsCount);
    }

    if (hasPaymentCounts) {
      this.rootStore.analyticsStore.sendEvent(
        AnalyticsEvent.paymentSuccessFirst
      );
      localStorageHandler.remove(
        LocalStorageKey.withoutPaymentsStateBeforePayment
      );
    }
  };

  private handleLSPaymentStateBeforePayment = (): void => {
    if (!this.info) {
      return;
    }

    const localStorageRawData = localStorageHandler.getItem(
      LocalStorageKey.paymentStateBeforePayment
    );

    if (!localStorageRawData) {
      return;
    }

    try {
      const dataBeforePayment = JSON.parse(localStorageRawData);

      if (
        dataBeforePayment.state &&
        Object.values(PaymentState).includes(dataBeforePayment.state) &&
        typeof dataBeforePayment.balance === 'number' &&
        dataBeforePayment.balance < this.info.billing.balance
      ) {
        const price = this.info.billing.balance - dataBeforePayment.balance;
        const { state, ...rest } = dataBeforePayment;
        const payload = {
          payment_state_before_payment: state,
          ...rest,
          price
        };

        this.rootStore.analyticsStore.sendEvent(
          AnalyticsEvent.paymentSuccess,
          payload
        );

        this.rootStore.analyticsStore.ym.addToQueue({
          type: AnalyticsEvent.paymentSuccessYm,
          payload: {
            order_price: price,
            currency: 'RUB'
          }
        });

        // на момент оплаты баланс 0 и нет привязанных карт
        if (dataBeforePayment.state === PaymentState.subscriptionAvailable) {
          this.rootStore.analyticsStore.sendEvent(
            AnalyticsEvent.paymentSuccessSubscriptionAvailable
          );
        }

        localStorageHandler.remove(LocalStorageKey.paymentStateBeforePayment);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('error while parse localstorage value');
      localStorageHandler.remove(LocalStorageKey.paymentStateBeforePayment);
    }
  };

  handleSuccessPaymentEvent = async (): Promise<void> => {
    this.handleLSPaymentStateBeforePayment();
    this.handleLSPaymentsCount();
  };

  activateSubscription = (): void => {
    if (!this.info) {
      return;
    }

    this.rootStore.analyticsStore.sendEvent(
      AnalyticsEvent.paymentSubscriptionActivation,
      {
        cabinet_origin: window.location.origin
      }
    );
    this.pay({ amount: Math.round(this.info.monthPrice) });
  };

  pay = async (params: PaymentParams): Promise<BaseResponse> => {
    const promisesResp = await Promise.allSettled([
      this.getLink(params),
      this.updatePaymentsCount()
    ]);

    const [linkResult] = promisesResp;

    if (linkResult.status === 'rejected') {
      return {
        isError: true
      };
    }

    const response = linkResult.value;

    if (response.isError) {
      return {
        isError: true
      };
    }

    this.rootStore.analyticsStore.sendEvent(AnalyticsEvent.paymentOpenKassa, {
      ...params,
      cabinet_origin: window.location.origin
    });
    this.rootStore.appNotificationsStore.openBanner({
      id: PAYMENT_LINK_BANNER_ID,
      type: AppNotificationType.warning,
      title: (
        <TypedTransComponent
          ns="stores"
          i18nKey="PaymentStore.notifications.windows"
        >
          Если у вас не открылась страница оплаты, перейдите по{' '}
          <ExternalLink
            href={response.data}
            fontType={TypographyType.text}
            fontSize={TypographySize.s}
            fontColor={TypographyColor.link}
          >
            ссылке
          </ExternalLink>
        </TypedTransComponent>
      ),
      priority: BannerPriority.extra
    });

    this.saveStateBeforeOpenPaymentWindow();

    window.location.href = response.data;

    return {
      isError: false
    };
  };

  getInfo = async (
    params?: GetPaymentInfoRequestParams
  ): Promise<BaseResponse> => {
    if (
      !this.rootStore.permissionsStore.canViewBilling ||
      this.getInfoStage.isLoading
    ) {
      return {
        isError: true
      };
    }

    this.getInfoStage.loading();

    const response = await this.rootStore.networkStore.api<PaymentInfoServer>(
      apiUrls.PAYMENTS_GET_INFO,
      { data: params }
    );

    if (response.isError) {
      this.getInfoStage.error();

      return {
        isError: true
      };
    }

    this.getInfoStage.success();
    runInAction(() => {
      // this._info должно изменяться только здесь
      this._info = PaymentInfoModel.fromJson(response.data, this.rootStore);
    });

    if (this._info) {
      this.availableSumPayments.changeValue(
        getAvailableSumPayments(this._info.minimalPayment)
      );
      // любое изменение this._info должно вызывать handleSuccessPaymentEvent
      this.handleSuccessPaymentEvent();
    }

    return {
      isError: false
    };
  };

  updatePaymentsCount = async (): Promise<BaseResponse> => {
    /** Если баланс 0 и нет карт -> обновляем info и получаем кол-во оплат -> вызывается handleSuccessPaymentEvent(если нужно отправляются события аналитики и очищается localStorage) */
    const lsData = getBooleanValueFromLS(
      LocalStorageKey.withoutPaymentsStateBeforePayment
    );

    const shouldGetPaymentsCount =
      this.info &&
      this.info.billing.balance <= 0 &&
      !this.info.methods.length &&
      !this._info?.paymentsCount &&
      lsData === null;

    if (shouldGetPaymentsCount) {
      return this.getInfo({ with_payments_count: true });
    }

    return { isError: false };
  };

  private getPaymentsCount = async (): Promise<
    BaseResponse<{ paymentsCount: number | null }>
  > => {
    if (!this.rootStore.permissionsStore.canViewBilling) {
      return {
        isError: true
      };
    }

    const response = await this.rootStore.networkStore.api<PaymentInfoServer>(
      apiUrls.PAYMENTS_GET_INFO,
      { data: { with_payments_count: true } }
    );

    if (response.isError) {
      return {
        isError: true
      };
    }

    return {
      isError: false,
      data: {
        paymentsCount: response.data.payments_count ?? null
      }
    };
  };

  manageBannerAboutCreatedInvoice = () => {
    if (!this.info) {
      return;
    }
    const LSKey = LocalStorageKey.createdInvoiceBannerWasClosed;
    const invoiceAutogeneratedDate = this.info.invoiceAutogeneratedDate;

    if (
      !invoiceAutogeneratedDate ||
      differenceInDays(new Date(), invoiceAutogeneratedDate) >
        DAYS_TO_SHOW_BANNER
    ) {
      localStorageHandler.remove(LSKey);
      return;
    }

    const daysLeft = this.info.daysLeft;

    if (daysLeft > 0 && daysLeft <= 3) {
      this.rootStore.appNotificationsStore.openBanner({
        type: AppNotificationType.success,
        excludePath: [urls.AUTH.mask],
        title: (t) =>
          t('PaymentStore.createdInvoiceBanner.paidPeriodWillEndText.text', {
            ns: 'stores',
            days: daysLeft,
            count: daysLeft
          }),
        id: CREATED_INVOICE_BANNER_ID,
        link: {
          title: (t) =>
            t('PaymentStore.createdInvoiceBanner.link', {
              ns: 'stores'
            }),
          to: urls.SETTINGS.CABINET.DOCUMENTS.create()
        },
        priority: BannerPriority.high
      });

      return;
    }

    const bannerWasClosed =
      getBooleanValueFromLS(LocalStorageKey.createdInvoiceBannerWasClosed) ??
      false;

    if (bannerWasClosed) {
      return;
    }

    this.rootStore.appNotificationsStore.openBanner({
      type: AppNotificationType.success,
      excludePath: [urls.AUTH.mask],
      title: (t) =>
        t('PaymentStore.createdInvoiceBanner.text', { ns: 'stores' }),
      id: CREATED_INVOICE_BANNER_ID,
      link: {
        title: (t) =>
          t('PaymentStore.createdInvoiceBanner.link', {
            ns: 'stores'
          }),
        to: urls.SETTINGS.CABINET.DOCUMENTS.create()
      },
      onClose: () => {
        localStorageHandler.setItem(LSKey, 'true');
      },
      priority: BannerPriority.high
    });
  };

  closeBannerAboutCreatedInvoice = () => {
    this.rootStore.appNotificationsStore.closeBanner(CREATED_INVOICE_BANNER_ID);
  };

  private getLink = async (
    params: PaymentParams
  ): Promise<BaseResponse<string>> => {
    if (this.getLinkStage.isLoading) {
      return {
        isError: true
      };
    }

    this.getLinkStage.loading();

    const response = await this.rootStore.networkStore.api<
      {
        redirect_url: string;
      },
      CalculatePayError
    >(apiUrls.PAYMENTS_PAY, {
      method: 'POST',
      data: params,
      errorsMap: mapCalculatePayErrorToMessage,
      showExpectedError: false // слева не будет появляться всплывашка для обработанных ошибок
    });

    if (!response.isError) {
      this.getLinkStage.success();

      return {
        isError: false,
        data: response.data.redirect_url
      };
    }

    this.paymentFill.handleError(response.data?.code || null);
    this.getLinkStage.error();
    return {
      isError: true
    };
  };

  initReactions = (): void => {
    this._paymentFill.initReactions();
  };

  disposeAll = (): void => {
    this._paymentFill.disposeAll();
  };
}
